import {
  CLAIM_STATES,
  type InstallationDto,
  type MeDto,
  PLAN_ANCHORS,
  type ServicePlanDto,
  type ServiceTicketDto,
  type TaskStatus,
  TICKET_PRIORITIES,
  TICKET_STATUSES,
  TICKET_TYPES,
  billingReasonRequired,
  type TicketBilling,
  type TicketType,
  warrantyText,
  warrantyValid,
} from '@bqcrm/shared';
import { Alert, Anchor, Badge, Button, Card, Drawer, Group, Modal, MultiSelect, Select, Stack, Table, Tabs, Text, Textarea, TextInput } from '@mantine/core';
import { IconAlertTriangle, IconPlus, IconSearch } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { can } from '../../auth';
import { api } from '../../api';
import { CompanyBadgeById, TaskStatusBadge } from '../../components/Badges';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { EmptyState, FilterChips } from '../../components/ui';
import { useCompanies } from '../../data';
import { notifyError, notifyOk } from '../../notify';
import { dateOnly, HealthBadge, TicketBadge, todayIso, WarrantyRisk } from './common';
import { BillingFields, TicketPanel } from './TicketPanel';

type PlanRow = ServicePlanDto & { installationName: string; siteAddress: string; clientName: string | null; tradeCode: string; deviceName: string | null };

/** Serwis: zgłoszenia (przegląd, awaria, reklamacja, naprawa gwarancyjna) i przeglądy do umówienia / zaległe. */
export function ServicePage({ me }: { me: MeDto }) {
  const [params, setParams] = useSearchParams();
  // QA qa10302243 P5: ?widok=reklamacje (pulpit) — zgłoszenia z reklamacją otwartą u producenta
  const [tab, setTab] = useState<string | null>(params.get('widok') === 'reklamacje' ? 'zgloszenia' : (params.get('widok') ?? 'zgloszenia'));
  const [status, setStatus] = useState(params.get('widok') === 'reklamacje' ? 'reklamacje' : 'otwarte');
  const [q, setQ] = useState('');
  const [planFilter, setPlanFilter] = useState('do_umowienia');
  const [creating, setCreating] = useState(false);
  const ticketId = params.get('zgloszenie');
  const tickets = useQuery({
    queryKey: ['service-tickets', status, q],
    queryFn: () =>
      api<ServiceTicketDto[]>(
        `/service-tickets?${new URLSearchParams({ ...(status === 'reklamacje' ? { claim: 'otwarte' } : status === 'niedoreczone' ? { protocols: 'niedoreczone' } : status !== 'wszystkie' ? { status } : {}), ...(q ? { q } : {}) })}`,
      ),
    enabled: tab === 'zgloszenia',
  });
  // QA6 P29: licznik reklamacji otwartych u producenta (także w zamkniętych zgłoszeniach)
  const claims = useQuery({ queryKey: ['service-tickets', 'reklamacje-licznik'], queryFn: () => api<ServiceTicketDto[]>('/service-tickets?claim=otwarte'), enabled: tab === 'zgloszenia' });
  // QA qa10302046 P20: zgłoszenia z protokołami niewysłanymi klientowi (np. klient bez adresu e-mail przy podpisie)
  const undelivered = useQuery({ queryKey: ['service-tickets', 'niedoreczone-licznik'], queryFn: () => api<ServiceTicketDto[]>('/service-tickets?protocols=niedoreczone'), enabled: tab === 'zgloszenia' });
  const plans = useQuery({ queryKey: ['service-plans', planFilter], queryFn: () => api<PlanRow[]>(`/service-plans?filter=${planFilter}`), enabled: tab === 'przeglady' });
  const open = (id: string | null) => setParams((p) => (id ? (p.set('zgloszenie', id), p) : (p.delete('zgloszenie'), p)));

  return (
    <>
      <PageHeader
        title="Zgłoszenia i przeglądy"
        description="Zgłoszenia serwisowe i plany przeglądów. Zgłoszenie przeglądu powstaje samo na 45 dni przed terminem (najwyżej jedno otwarte na plan); wizytę planuje się przyciskiem „Zaplanuj wizytę” w zgłoszeniu."
        actions={
          can(me, 'serwis.tworzenie', 'W') && (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>
              Nowe zgłoszenie
            </Button>
          )
        }
      />
      <Tabs value={tab} onChange={setTab} mb="md">
        <Tabs.List>
          <Tabs.Tab value="zgloszenia">Zgłoszenia</Tabs.Tab>
          <Tabs.Tab value="przeglady">Przeglądy</Tabs.Tab>
        </Tabs.List>
      </Tabs>
      {tab === 'zgloszenia' ? (
        <>
          <Group mb="sm" wrap="wrap">
            <FilterChips
              value={status}
              onChange={setStatus}
              options={[
                { value: 'otwarte', label: 'Otwarte' },
                { value: 'do_umowienia', label: 'Do umówienia' },
                { value: 'wykonane', label: 'Wykonane' },
                { value: 'reklamacje', label: `Reklamacje u producenta${claims.data?.length ? ` (${claims.data.length})` : ''}` },
                ...(undelivered.data?.length || status === 'niedoreczone' ? [{ value: 'niedoreczone', label: `Protokoły niedoręczone${undelivered.data?.length ? ` (${undelivered.data.length})` : ''}` }] : []),
                { value: 'wszystkie', label: 'Wszystkie' },
              ]}
            />
            <TextInput leftSection={<IconSearch size={16} />} placeholder="Numer, adres, klient, opis" value={q} onChange={(e) => setQ(e.currentTarget.value)} w={{ base: '100%', sm: 280 }} />
          </Group>
          <Card padding={0}>
          <QueryState isLoading={tickets.isLoading} error={tickets.error}>
            <Table.ScrollContainer minWidth={900}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Numer</Table.Th>
                    <Table.Th>Rodzaj</Table.Th>
                    <Table.Th>Stan</Table.Th>
                    <Table.Th>Klient / adres</Table.Th>
                    <Table.Th>Termin</Table.Th>
                    <Table.Th>Firma</Table.Th>
                    <Table.Th>Wizyta</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {tickets.data?.map((t) => (
                    <Table.Tr key={t.id} className="row-link" onClick={() => open(t.id)}>
                      <Table.Td>
                        <Text size="sm" fw={600}>
                          {t.number}
                        </Text>
                        {t.warranty && (
                          <Badge size="xs" color="grape" variant="light">
                            Gwarancyjne
                          </Badge>
                        )}
                        {(t.undeliveredProtocolIds?.length ?? 0) > 0 && (
                          <Text size="xs" c="orange.8">
                            Protokół niedoręczony{t.clientEmail ? ' — klient ma już e-mail' : ''}
                          </Text>
                        )}
                        {t.claimState !== 'brak' && (
                          <Text size="xs" c={t.claimOverdue ? 'red.7' : t.claimState === 'zgloszone' || t.claimState === 'zaakceptowane' ? 'orange.8' : 'dimmed'}>
                            Producent: {CLAIM_STATES[t.claimState]}
                            {t.claimNumber ? ` (${t.claimNumber})` : ''}
                            {t.claimDueOn ? ` · decyzja do ${dateOnly(t.claimDueOn)}${t.claimOverdue ? ' — sprawdź' : ''}` : ''}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>{TICKET_TYPES[t.type]}</Table.Td>
                      <Table.Td>
                        <TicketBadge status={t.status} />
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{t.clientName ?? '—'}</Text>
                        <Text size="xs" c="dimmed">
                          {t.siteAddress}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c={t.overdue ? 'red' : undefined} fw={t.overdue ? 700 : undefined}>
                          {dateOnly(t.dueDate)}
                        </Text>
                      </Table.Td>
                      <Table.Td>{t.companyId ? <CompanyBadgeById id={t.companyId} /> : '—'}</Table.Td>
                      <Table.Td>
                        {(() => {
                          const v = t.tasks.find((x) => ['do_zaplanowania', 'zaplanowane', 'w_toku', 'wstrzymane'].includes(x.status));
                          if (!v) return <Text size="xs" c="dimmed">{t.status === 'wykonane' || t.status === 'zamkniete' ? 'wykonana' : 'brak terminu'}</Text>;
                          if (!v.dateFrom)
                            return (
                              <Text size="xs" c="orange">
                                do zaplanowania
                              </Text>
                            );
                          return (
                            <Group gap={6} wrap="nowrap">
                              <Text size="xs" fw={600}>
                                {dateOnly(v.dateFrom)}
                              </Text>
                              <TaskStatusBadge status={v.status as TaskStatus} size="xs" />
                            </Group>
                          );
                        })()}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            {tickets.data?.length === 0 && <EmptyState title="Brak zgłoszeń w tym widoku" hint={q ? 'Zmień wyszukiwane słowa albo wybierz inny widok.' : undefined} />}
          </QueryState>
          </Card>
        </>
      ) : (
        <>
          <Group mb="sm" gap="sm" wrap="wrap">
            <FilterChips
              value={planFilter}
              onChange={setPlanFilter}
              options={[
                { value: 'do_umowienia', label: 'Do umówienia' },
                { value: 'zalegle', label: 'Po terminie' },
                { value: 'wszystkie', label: 'Wszystkie' },
              ]}
            />
            <Text size="xs" c="dimmed">
              {planFilter === 'do_umowienia' ? 'Przeglądy do umówienia, także po terminie.' : planFilter === 'zalegle' ? 'Po terminie: w tolerancji i zaległe.' : 'Wszystkie plany, także w terminie i wstrzymane.'}
            </Text>
          </Group>
          <Card padding={0}>
          <QueryState isLoading={plans.isLoading} error={plans.error}>
            <Table.ScrollContainer minWidth={900}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Termin</Table.Th>
                    <Table.Th>Stan</Table.Th>
                    <Table.Th>Instalacja / urządzenie</Table.Th>
                    <Table.Th>Klient / adres</Table.Th>
                    <Table.Th>Cykl</Table.Th>
                    <Table.Th>Zgłoszenie</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {plans.data?.map((p) => (
                    <Table.Tr key={p.id}>
                      <Table.Td>
                        <Text size="sm" fw={600}>
                          {dateOnly(p.due)}
                        </Text>
                        {p.dueOverride && (
                          <Text size="xs" c="dimmed">
                            przesunięty jednorazowo
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <HealthBadge health={p.health} />
                        {p.warrantyRequiresInspections && (p.health === 'zalegly' || p.health === 'po_terminie') && <WarrantyRisk />}
                      </Table.Td>
                      <Table.Td>
                        <Anchor component={Link} to={`/instalacje?id=${p.installationId}`} size="sm">
                          {p.installationName}
                        </Anchor>
                        {p.deviceName && (
                          <Text size="xs" c="dimmed">
                            {p.deviceName}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{p.clientName ?? '—'}</Text>
                        <Text size="xs" c="dimmed">
                          {p.siteAddress}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs">
                          co {p.cycleValue} {p.cycleUnit === 'miesiace' ? 'mies.' : 'dni'} · {p.anchor === 'wykonanie' ? 'od wykonania' : 'stały rytm'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {p.openTicketId ? (
                          <Anchor component="button" type="button" size="sm" onClick={() => (setTab('zgloszenia'), open(p.openTicketId))}>
                            {p.openTicketNumber}
                          </Anchor>
                        ) : (
                          <Text size="xs" c="dimmed">
                            {p.active ? 'powstanie na 45 dni przed terminem' : `wstrzymany: ${p.pauseReason}`}
                          </Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            {plans.data?.length === 0 && <EmptyState title="Brak planów w tym widoku" />}
          </QueryState>
          </Card>
          <Text size="xs" c="dimmed" mt="xs">
            Tryby terminów: {Object.values(PLAN_ANCHORS).join(' · ')}. Zaległy przegląd nie przesuwa się sam — biuro dostaje cotygodniowe przypomnienie.
          </Text>
        </>
      )}
      <Drawer opened={!!ticketId} onClose={() => open(null)} position="right" size="lg" title="Zgłoszenie serwisowe">
        {ticketId && <TicketPanel id={ticketId} me={me} />}
      </Drawer>
      <Modal opened={creating} onClose={() => setCreating(false)} title="Nowe zgłoszenie serwisowe" size="lg">
        {creating && (
          <TicketForm
            onDone={(id) => {
              setCreating(false);
              if (id) open(id);
            }}
          />
        )}
      </Modal>
    </>
  );
}

interface SiteOption {
  id: string;
  address: string;
  clientName: string | null;
  clientId?: string | null;
  clientEmail?: string | null;
  clientPhone?: string | null;
  caseNumber?: string | null;
}

/** Pozycja listy: klient — adres — telefon — ostatnia sprawa. */
const siteLabel = (s: SiteOption) => [s.clientName, s.address, s.clientPhone, s.caseNumber].filter(Boolean).join(' — ');

function TicketForm({ onDone }: { onDone: (id: string | null) => void }) {
  const qc = useQueryClient();
  const companies = useCompanies();
  const [search, setSearch] = useState('');
  const [siteId, setSiteId] = useState<string | null>(null);
  const [f, setF] = useState({ type: 'awaria' as TicketType, installationId: null as string | null, deviceIds: [] as string[], priority: 'normalny', reporter: '', description: '', warranty: false, companyId: null as string | null });
  // rozliczenie naprawy od razu przy dodaniu (null — wg gwarancji urządzenia)
  const [billing, setBilling] = useState<{ billing: TicketBilling | null; reason: string }>({ billing: null, reason: '' });
  const shownBilling = billing.billing ?? (f.warranty ? 'gwarancja' : null);
  const reasonMissing = !!billing.billing && billingReasonRequired(billing.billing, false) && billing.reason.trim().length < 3;
  // wyszukiwanie: klient, adres, sprawa, telefon; po wyborze pole pokazuje etykietę wybranej pozycji — nie szukamy po niej
  const [pickedLabel, setPickedLabel] = useState<string | null>(null);
  const [term] = useDebouncedValue(search === pickedLabel ? '' : search.trim(), 250);
  const sites = useQuery({ queryKey: ['service-sites', term], queryFn: () => api<SiteOption[]>(`/service/sites?q=${encodeURIComponent(term)}`), placeholderData: (prev) => prev });
  // QA qa10302243 P6: klient bez adresu e-mail — protokół z wizyty nie pójdzie e-mailem; adres można dopisać tutaj
  const [picked, setPicked] = useState<SiteOption | null>(null);
  const [clientEmail, setClientEmail] = useState('');
  const noEmail = !!picked?.clientId && !picked.clientEmail;
  const emailOk = !clientEmail.trim() || /^[^s@]+@[^s@]+.[^s@]+$/.test(clientEmail.trim());
  const insts = useQuery({ queryKey: ['site-installations', siteId], queryFn: () => api<InstallationDto[]>(`/sites/${siteId}/installations`), enabled: !!siteId });
  const inst = insts.data?.find((i) => i.id === f.installationId);
  // D16: gwarancja zaznacza się sama przy urządzeniu z ważną gwarancją — dopóki użytkownik nie zmieni pola ręcznie
  const [warrantyTouched, setWarrantyTouched] = useState(false);
  const today = todayIso();
  const warrantyDevice = inst?.devices.filter((d) => f.deviceIds.includes(d.id) && warrantyValid(d.warrantyUntil, today)).sort((a, b) => (b.warrantyUntil ?? '').localeCompare(a.warrantyUntil ?? ''))[0];
  const pickDevices = (ids: string[]) => {
    const valid = inst?.devices.some((d) => ids.includes(d.id) && warrantyValid(d.warrantyUntil, today)) ?? false;
    setF({ ...f, deviceIds: ids, warranty: warrantyTouched ? f.warranty : valid });
  };
  // U4: otwarte zgłoszenia i zaległe przeglądy tej instalacji — do załatwienia przy jednej wizycie
  const openTickets = useQuery({
    queryKey: ['service-tickets', 'instalacja', f.installationId],
    queryFn: () => api<ServiceTicketDto[]>(`/service-tickets?status=otwarte&installationId=${f.installationId}`),
    enabled: !!f.installationId,
  });
  const latePlans = (inst?.plans ?? []).filter((p) => p.health === 'zalegly' || p.health === 'po_terminie');
  const save = useMutation({
    mutationFn: () => api<ServiceTicketDto>('/service-tickets', { method: 'POST', body: { ...f, siteId, clientEmail: noEmail && clientEmail.trim() ? clientEmail.trim() : null, ...(billing.billing ? { billing: billing.billing, billingReason: billing.reason } : {}) } }),
    onSuccess: (t) => {
      notifyOk(`Utworzono zgłoszenie ${t.number}`);
      void qc.invalidateQueries({ queryKey: ['service-tickets'] });
      onDone(t.id);
    },
    onError: notifyError,
  });
  return (
    <Stack>
      <Select
        label="Klient / lokalizacja"
        placeholder="Szukaj: klient, adres, sprawa, telefon"
        searchable
        searchValue={search}
        onSearchChange={setSearch}
        filter={({ options }) => options}
        data={[...(sites.data ?? []), ...(picked && !sites.data?.some((s) => s.id === picked.id) ? [picked] : [])].map((s) => ({ value: s.id, label: siteLabel(s) }))}
        value={siteId}
        onChange={(v) => {
          const s = sites.data?.find((x) => x.id === v) ?? null;
          setSiteId(v);
          setPicked(s);
          setPickedLabel(s ? siteLabel(s) : null);
          setClientEmail('');
          setF({ ...f, installationId: null, deviceIds: [] });
        }}
        nothingFoundMessage={term ? 'Nie znaleziono — sprawdź pisownię albo dodaj lokalizację w karcie klienta' : 'Wpisz klienta, adres, numer sprawy albo telefon'}
      />
      {noEmail && (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />} title="Klient nie ma e-maila">
          <Stack gap={6}>
            <Text size="sm">{picked?.clientName ?? 'Klient'} nie dostanie protokołu z wizyty e-mailem. Dopisz adres albo wręcz protokół na miejscu (serwisant zaznaczy to przy podpisie).</Text>
            <TextInput
              size="xs"
              placeholder="adres e-mail klienta (opcjonalnie)"
              value={clientEmail}
              error={emailOk ? undefined : 'Nieprawidłowy adres e-mail'}
              onChange={(e) => setClientEmail(e.currentTarget.value)}
              description={clientEmail.trim() ? 'Zapisze się w karcie klienta razem ze zgłoszeniem' : undefined}
            />
          </Stack>
        </Alert>
      )}
      <Select
        label="Instalacja"
        disabled={!siteId}
        clearable
        data={(insts.data ?? []).map((i) => ({ value: i.id, label: `${i.name} (${i.tradeName})` }))}
        value={f.installationId}
        onChange={(v) => setF({ ...f, installationId: v, deviceIds: [], warranty: warrantyTouched ? f.warranty : false })}
        description={siteId && insts.data?.length === 0 ? 'W tej lokalizacji nie ma jeszcze instalacji w systemie' : undefined}
      />
      {inst && (
        <MultiSelect
          label="Urządzenia"
          data={inst.devices
            .filter((d) => d.status === 'czynne')
            .map((d) => ({ value: d.id, label: [`${d.name}${d.serialNumber ? ` (SN ${d.serialNumber})` : ''}`, warrantyText(d.warrantyUntil, today)].filter(Boolean).join(' · ') }))}
          value={f.deviceIds}
          onChange={pickDevices}
        />
      )}
      {inst && ((openTickets.data ?? []).length > 0 || latePlans.length > 0) && (
        <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="Na tej instalacji czeka już">
          <Stack gap={2}>
            {(openTickets.data ?? []).map((t) => (
              <Text key={t.id} size="sm">
                Zgłoszenie{' '}
                <Anchor component={Link} to={`/serwis?zgloszenie=${t.id}`} size="sm">
                  {t.number}
                </Anchor>{' '}
                — {TICKET_TYPES[t.type].toLowerCase()}, {TICKET_STATUSES[t.status].toLowerCase()}
              </Text>
            ))}
            {latePlans.map((p) => (
              <Text key={p.id} size="sm">
                {p.health === 'zalegly' ? 'Zaległy przegląd' : 'Przegląd po terminie'}
                {p.deviceId ? ` (${inst.devices.find((d) => d.id === p.deviceId)?.name ?? 'urządzenie'})` : ''} — termin {dateOnly(p.due)}
                {p.warrantyRequiresInspections ? ' · ryzyko utraty gwarancji' : ''}
                {p.openTicketNumber ? ` · zgłoszenie ${p.openTicketNumber}` : ''}{' '}
                <Anchor component={Link} to={`/instalacje?id=${inst.id}`} size="sm">
                  karta instalacji
                </Anchor>
              </Text>
            ))}
            <Text size="xs" c="dimmed">
              Każde zgłoszenie ma osobną wizytę i protokół — system nie łączy ich w jedną. Zaległy przegląd ekipa zobaczy w telefonie przy tej wizycie; jeśli go wykona przy okazji, oznacz zgłoszenie przeglądu „Wykonane bez wizyty w aplikacji” (gwarancja może wymagać przeglądów).
            </Text>
          </Stack>
        </Alert>
      )}
      <Group grow>
        <Select label="Rodzaj" data={Object.entries(TICKET_TYPES).map(([value, label]) => ({ value, label }))} value={f.type} onChange={(v) => v && setF({ ...f, type: v as TicketType })} />
        <Select label="Priorytet" data={Object.entries(TICKET_PRIORITIES).map(([value, label]) => ({ value, label }))} value={f.priority} onChange={(v) => v && setF({ ...f, priority: v })} />
      </Group>
      <TextInput label="Zgłaszający" value={f.reporter} onChange={(e) => setF({ ...f, reporter: e.currentTarget.value })} placeholder="np. klient telefonicznie" />
      <Textarea label="Opis" required autosize minRows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.currentTarget.value })} />
      <Group grow align="flex-start">
        <Stack gap="xs">
          <BillingFields
            billing={shownBilling}
            reason={billing.reason}
            needReason={!!billing.billing && billingReasonRequired(billing.billing, false)}
            hint={!billing.billing && f.warranty && warrantyDevice && !warrantyTouched ? `gwarancja, bo ${warrantyDevice.name} ma gwarancję do ${dateOnly(warrantyDevice.warrantyUntil)}` : undefined}
            onChange={(b, reason) => {
              setWarrantyTouched(true);
              setBilling({ billing: b, reason });
              setF({ ...f, warranty: b === 'gwarancja' });
            }}
          />
        </Stack>
        <Select
          label="Firma rozliczająca"
          clearable
          placeholder="domyślnie: firma, która sprzedała urządzenie"
          data={(companies.data ?? []).map((c) => ({ value: c.id, label: c.shortName }))}
          value={f.companyId}
          onChange={(v) => setF({ ...f, companyId: v })}
        />
      </Group>
      <Group justify="flex-end">
        <Button variant="default" onClick={() => onDone(null)}>
          Anuluj
        </Button>
        <Button disabled={!siteId || f.description.trim().length < 3 || !emailOk || reasonMissing} loading={save.isPending} onClick={() => save.mutate()}>
          Dodaj zgłoszenie
        </Button>
      </Group>
    </Stack>
  );
}
